import { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ErrorState } from '../../../shared/components/ErrorState';
import { describeApiError } from '../../../shared/api/errors';
import { type useEmployeeValesHooks } from './hooks';
import { EmployeeValeParcelaBaixarDialog } from './EmployeeValeParcelaBaixarDialog';
import type { EmployeeValeDto, EmployeeValeParcelaDto, ValeStatus } from './types';

export interface EmployeeValeParcelasDialogProps {
  open: boolean;
  vale: EmployeeValeDto | null;
  hooks: ReturnType<typeof useEmployeeValesHooks>;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

function formatBRL(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function statusChipColor(status: ValeStatus): 'default' | 'success' | 'warning' {
  if (status === 'PAG') return 'success';
  if (status === 'CAN') return 'default';
  return 'warning';
}

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'id',
  sortOrder: 'asc',
};

/**
 * Dialog que lista as parcelas do vale (Fase 1 grandchild read-only) com ação
 * de **baixa** por parcela (Fase 2 Pacote A — card [h7TE7xcf]).
 *
 * Ação Baixar fica disponível só pra parcelas em `ABE`. Se a baixa atual for
 * a última em aberto, o header do vale transita pra `PAG` server-side e a
 * mutation invalida a list pra refletir.
 */
export function EmployeeValeParcelasDialog({
  open,
  vale,
  hooks,
  onClose,
}: EmployeeValeParcelasDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { query, setQuery, clear } = usePersistedGridState('colaborador-vale-parcelas', DEFAULT_QUERY, {});
  const [baixando, setBaixando] = useState<EmployeeValeParcelaDto | null>(null);

  const parcelasQuery = hooks.useParcelas(vale?.id ?? null);
  const rows = parcelasQuery.data?.data ?? [];
  const parcelasEmAberto = rows.filter((p) => p.status === 'ABE').length;

  const columns: GridColDef<EmployeeValeParcelaDto>[] = [
    {
      field: 'id',
      headerName: t('features.colaboradores.vales.parcelas.columns.numero'),
      width: 80,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'dataVencimento',
      headerName: t('features.colaboradores.vales.parcelas.columns.dataVencimento'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'valor',
      headerName: t('features.colaboradores.vales.parcelas.columns.valor'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => formatBRL(value),
    },
    {
      field: 'dataPagamento',
      headerName: t('features.colaboradores.vales.parcelas.columns.dataPagamento'),
      width: 130,
      valueFormatter: (value: string | null) => formatDate(value),
    },
    {
      field: 'valorPago',
      headerName: t('features.colaboradores.vales.parcelas.columns.valorPago'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null) => formatBRL(value),
    },
    {
      field: 'status',
      headerName: t('features.colaboradores.vales.parcelas.columns.status'),
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(`features.colaboradores.vales.status.${params.row.status}`)}
          color={statusChipColor(params.row.status)}
          variant={params.row.status === 'ABE' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const podeBaixar = params.row.status === 'ABE';
        return (
          <Tooltip
            title={
              podeBaixar
                ? t('features.colaboradores.vales.parcelas.actions.baixar')
                : t('features.colaboradores.vales.parcelas.actions.baixarDisabledTooltip')
            }
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!podeBaixar}
                aria-label={t('features.colaboradores.vales.parcelas.actions.baixarAria', {
                  n: params.row.id,
                })}
                onClick={() => setBaixando(params.row)}
              >
                <PaymentIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {vale
            ? t('features.colaboradores.vales.parcelas.dialogTitleNamed', { id: vale.id })
            : t('features.colaboradores.vales.parcelas.dialogTitle')}
        </DialogTitle>
        <DialogContent dividers>
          {parcelasQuery.isLoadingError ? (
            <ErrorState
              description={describeApiError(parcelasQuery.error)}
              onRetry={() => parcelasQuery.refetch()}
            />
          ) : (
            <Stack spacing={2}>
              {vale && (
                <Typography variant="body2" color="text.secondary">
                  {t('features.colaboradores.vales.parcelas.headerSummary', {
                    valor: formatBRL(vale.valor),
                    nroParcelas: vale.nroParcelas,
                    primeiroVencimento: formatDate(vale.vencimento1aParcela),
                  })}
                </Typography>
              )}
              <DataTable<EmployeeValeParcelaDto>
                rows={rows}
                columns={columns}
                total={parcelasQuery.data?.meta.total ?? rows.length}
                loading={parcelasQuery.isLoading || parcelasQuery.isFetching}
                query={query}
                onQueryChange={setQuery}
                onClear={clear}
                searchPlaceholder={t('features.colaboradores.vales.parcelas.searchPlaceholder')}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <EmployeeValeParcelaBaixarDialog
        open={baixando !== null}
        parcela={baixando}
        parcelasEmAbertoAntes={parcelasEmAberto}
        useBaixarParcela={hooks.useBaixarParcela}
        onClose={() => setBaixando(null)}
      />
    </>
  );
}
